import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../services/api"; // Import your existing API instance
import { isUniversityManagerDemoPath } from "../utils/universityManagerDemo";

const NetworkStatusContext = createContext({});

export const useNetworkStatus = () => {
  const context = useContext(NetworkStatusContext);
  if (!context) {
    throw new Error(
      "useNetworkStatus must be used within a NetworkStatusProvider"
    );
  }
  return context;
};

export const NetworkStatusProvider = ({ children }) => {
  const [isServerOnline, setIsServerOnline] = useState(null); // null = checking, true = online, false = offline
  const [isChecking, setIsChecking] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const checkServerStatus = useCallback(async () => {
    if (isChecking) return isServerOnline;

    // /uni-admin has its own backend/auth and doesn't depend on the main
    // app's server-status endpoint at all, so a failure/timeout here (common
    // on a cold load in a flaky in-app browser, e.g. opened from WhatsApp)
    // must never redirect that tree away to /network-error before its own
    // login/auth state has even resolved.
    const skipRedirect =
      location.pathname === "/network-error" ||
      isUniversityManagerDemoPath(location.pathname) ||
      location.pathname.startsWith("/uni-admin");

    setIsChecking(true);
    try {
      const response = await api.get("/api/app-config/server-status");

      const serverOk = response.data?.data?.status === "ok";
      setIsServerOnline(serverOk);

      if (!serverOk && !skipRedirect) {
        navigate("/network-error", { replace: true });
      }

      return serverOk;
    } catch (error) {
      console.error("Server status check failed:", error);
      setIsServerOnline(false);

      if (!skipRedirect) {
        navigate("/network-error", { replace: true });
      }

      return false;
    } finally {
      setIsChecking(false);
    }
  }, [isChecking, isServerOnline, navigate, location.pathname]);

  const handleNetworkError = useCallback(() => {
    if (
      location.pathname !== "/network-error" &&
      !isUniversityManagerDemoPath(location.pathname) &&
      !location.pathname.startsWith("/uni-admin")
    ) {
      navigate("/network-error", { replace: true });
    }
  }, [navigate, location.pathname]);

  // Initial server status check
  useEffect(() => {
    checkServerStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodic server status check (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      if (
        location.pathname !== "/network-error" &&
        !isUniversityManagerDemoPath(location.pathname) &&
        !location.pathname.startsWith("/uni-admin")
      ) {
        checkServerStatus();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [checkServerStatus, location.pathname]);

  const value = {
    isServerOnline,
    isChecking,
    checkServerStatus,
    handleNetworkError,
  };

  return (
    <NetworkStatusContext.Provider value={value}>
      {children}
    </NetworkStatusContext.Provider>
  );
};
